import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { SensorPortCommunicationMode } from "../../Configurations/portConfigs.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import type { EntityWithSensorAddressBase } from "../baseEntityTypes/entityWithSensorAddressBase.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    SensorClusterAddressFieldsWithDefaults,
    SensorClusterAddressSchemaTypes,
} from "../types/sensorClusterAddress.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { ForeignLookup } from "./foreignLookup.js";

const SCHEMA_KEY = "SensorClusterAddress" as const;

/**
 * The address that identifies where a SensorCluster lives on its host device.
 *
 * Three coordinates pin a cluster down: the host entity (`hostReference`), the port on
 * that host (`portId`), and the channel within that port (`channel`). `communicationMode`
 * tells consumers how to interpret the channel — always 0 for DIRECT-mode sensors,
 * a bus address for SDI-12 / RS-485.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SensorClusterAddress>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SensorClusterAddress
    extends SchemaBase<SensorClusterAddress, SensorClusterAddressFieldsWithDefaults>
    implements EntityWithSensorAddressBase
{
    static readonly schemaKey: string = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey: string = SCHEMA_KEY;

    /** Reference to the host entity (device, zone, etc.) the cluster is attached to. */
    @validate({
        zodValidator: z.object({
            targetId: z.string(),
            targetForeignField: z.string(),
            targetCollection: z.string(),
        }),
    })
    @prop({ type: () => ForeignLookup, required: true, _id: false })
    public hostReference!: ForeignLookup;

    /** The port on the host where the cluster is connected (e.g. "S1", "I1"). */
    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public portId!: string;

    /**
     * Communication protocol used between the host and the sensor (DIRECT, SDI-12, RS-485).
     * On SensorCluster this is server-set via a pre-save hook (response-only in the DTO);
     * on SensorElement this is propagated from the parent cluster.
     */
    @validate({
        zodValidator: z.enum(Object.values(SensorPortCommunicationMode) as [string, ...string[]]),
        responseOnly: true,
    })
    @prop({ type: () => String, enum: SensorPortCommunicationMode, required: true })
    public communicationMode!: SensorPortCommunicationMode;

    /** Channel within the port. 0 for DIRECT; bus address for SDI-12/RS-485. */
    @validate({ zodValidator: z.number().int().min(0) })
    @prop({ type: () => Number, required: true })
    public channel!: number;

    static getters = {} as const satisfies Record<string, (self: SensorClusterAddress) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensorClusterAddressSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SensorClusterAddress, SensorClusterAddressFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SensorClusterAddress.dtoValidators = getZodSchemaFromClass(
    SensorClusterAddress,
) as unknown as DtoValidatorTypes<SensorClusterAddressSchemaTypes>;
